import React, {useEffect, useState} from 'react'
import {Button} from 'react-bootstrap'
import axios from 'axios'
import Select from 'react-select'
import {CommonLoading} from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'
import {DropDownMenu} from '../quick-dials/QuickDialsView'

const API_URL = process.env.REACT_APP_API_URL

type IvrOption = { value: string; label: string; assignee?: string | null }

type RequesterQuickDialViewProps = {
  userId: string
  customerCode?: string | number
}

export function RequesterQuickDialView({userId, customerCode}: RequesterQuickDialViewProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [languages, setLanguages] = useState<{key: string; value: string}[]>([])
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false)
  const [isLoadingIvr, setIsLoadingIvr] = useState(false)
  const [ivrOptions, setIvrOptions] = useState<IvrOption[]>([])
  const [requesterHasQuickDial, setRequesterHasQuickDial] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  const [ivrNumber, setIvrNumber] = useState<string | null>(null)
  const [langs, setLangs] = useState<(string | null)[]>(Array(10).fill(null))

  const fetchCustomerLanguages = async (customerCodeParam: string | number | undefined) => {
    if (!customerCodeParam) return
    setIsLoadingLanguages(true)
    try {
      const response = await axios.get(`${API_URL}/customer/${customerCodeParam}/languages`)
      const responseData = response?.data?.result || []
      const transformed = Array.isArray(responseData)
        ? responseData.map((item: any) => ({
            key: item.code,
            value: item.name,
          }))
        : []
      setLanguages([...transformed, {key: 'OPI', value: 'Operator'}])
    } catch (error) {
      console.error(error)
      setLanguages([])
    } finally {
      setIsLoadingLanguages(false)
    }
  }

  const fetchIvrNumbers = async () => {
    setIsLoadingIvr(true)
    try {
      const response = await axios.get(`${API_URL}/master/ivr-numbers`, {
        params: {page: 1, items_per_page: 1000},
      })
      const data = response?.data?.data || []
      const options = data
        .filter(
          (d: {isDeleted: boolean; isGlobal?: boolean; IsGlobal?: boolean}) =>
            !d.isDeleted && !(d.isGlobal ?? d.IsGlobal)
        )
        .map(
          (d: {
            value: string
            code?: string
            Code?: string
            assignee?: string | null
            Assignee?: string | null
          }) => {
            const code = d.code ?? d.Code ?? d.value
            return {
              value: d.value,
              assignee: d.assignee ?? d.Assignee ?? null,
              label: `${code} - ${d.value}`,
            }
          }
        )
      setIvrOptions(options)
    } catch (error) {
      console.error(error)
      setIvrOptions([])
    } finally {
      setIsLoadingIvr(false)
    }
  }

  const getAvailableIvrOptions = () =>
    ivrOptions.filter((opt) => !opt.assignee || opt.value === ivrNumber)

  const getExistingConfig = async (requesterId: string | undefined) => {
    setIsLoading(true)
    if (!requesterId) return
    try {
      const response = await axios.get(
        `${API_URL}/requesterhasquickdial/GetByRequesterId/${requesterId}`
      )
      setRequesterHasQuickDial(response?.data?.requesterHasQuickDial ?? false)
      setIsScheduled(!!response?.data?.isScheduled)
      setIvrNumber(response?.data?.ivrNumber ?? null)
      const loaded = response?.data?.languages
      if (Array.isArray(loaded)) {
        const padded = [...loaded]
        while (padded.length < 10) padded.push(null)
        setLangs(padded.slice(0, 10))
      } else {
        setLangs(Array(10).fill(null))
      }
    } catch (error) {
      console.error(error)
      toaster('error', 'Error fetching requester quick dial settings')
    } finally {
      setIsLoading(false)
    }
  }

  const saveConfig = async () => {
    if (!userId) return
    if (requesterHasQuickDial && !ivrNumber) {
      toaster('error', 'Please select a dedicated IVR number.')
      return
    }
    setIsLoading(true)
    try {
      const res = await axios.put(`${API_URL}/requesterhasquickdial/UpdateByRequesterId`, {
        requesterId: userId,
        requesterHasQuickDial,
        isScheduled,
        ivrNumber,
        languages: langs,
      })
      if (res?.data?.status === 'E') {
        toaster('error', res?.data?.text ?? 'Unable to update requester quick dial')
        return
      }
      toaster('success', 'Requester quick dial updated successfully')
      await Promise.all([getExistingConfig(userId), fetchIvrNumbers()])
    } catch (error: any) {
      const msg = error?.response?.data?.text ?? 'Unable to update requester quick dial'
      toaster('error', msg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (userId) getExistingConfig(userId)
  }, [userId])

  useEffect(() => {
    if (customerCode !== undefined && customerCode !== null && customerCode !== '') {
      fetchCustomerLanguages(customerCode)
    }
  }, [customerCode])

  useEffect(() => {
    fetchIvrNumbers()
  }, [])

  return (
    <div className='card-body p-0'>
      <div className='card-title d-flex align-items-center me-4 mb-3'>
        <div className='d-flex flex-column'>
          <h5 className='text-black fs-4 fw-semibold mb-0'>Quick Dial</h5>
          <p className='text-gray-500 mb-0 mt-4 small w-100'>
            Enable a dedicated IVR number for this requester with custom quick dial language
            options. When enabled, callers dialing that number are verified without a PIN.
          </p>
        </div>
      </div>

      {isLoading || isLoadingLanguages || isLoadingIvr ? (
        <CommonLoading />
      ) : (
        <>
          <div className='form-check form-switch d-flex align-items-center mt-5'>
            <input
              className='form-check-input w-45px h-30px cursor-pointer'
              type='checkbox'
              id='requesterHasQuickDial'
              checked={requesterHasQuickDial}
              onChange={() => {
                const next = !requesterHasQuickDial
                setRequesterHasQuickDial(next)
                if (!next) setIsScheduled(false)
              }}
            />
            <label
              className='form-check-label ms-3 text-muted fs-5 cursor-pointer'
              htmlFor='requesterHasQuickDial'
            >
              Enable Quick Dial
            </label>
          </div>

          <div className='form-check form-switch d-flex align-items-center mt-4'>
            <input
              className='form-check-input w-45px h-30px cursor-pointer'
              type='checkbox'
              id='requesterIsScheduled'
              checked={isScheduled}
              disabled={!requesterHasQuickDial}
              onChange={() => setIsScheduled(!isScheduled)}
            />
            <label
              className={`form-check-label ms-3 fs-5 ${requesterHasQuickDial ? 'text-muted cursor-pointer' : 'text-gray-400'}`}
              htmlFor='requesterIsScheduled'
            >
              Scheduled Function
            </label>
          </div>

          {requesterHasQuickDial && (
            <>
              <div className='mt-5'>
                <label className='form-label fw-bold text-black fs-6'>Dedicated Number</label>
                <Select
                  placeholder='Select an IVR Number'
                  value={
                    getAvailableIvrOptions().find((opt) => opt.value === (ivrNumber ?? '')) || null
                  }
                  onChange={(option: {value: string} | null) => setIvrNumber(option?.value ?? null)}
                  options={getAvailableIvrOptions()}
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      minHeight: '38px',
                    }),
                  }}
                />
              </div>

              <div className='mt-5'>
                <table className='table align-middle'>
                  <thead>
                    <tr>
                      <th className='fs-3 fw-bold'>Dialer Code</th>
                      <th className='fs-3 fw-bold'>Languages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({length: 9}).map((_, idx) => (
                      <tr key={idx}>
                        <td className='fs-3 text-center'>
                          <span className='d-inline-block w-120px fw-bold fs-3'>{idx + 1}</span>
                        </td>
                        <td>
                          <DropDownMenu
                            currentIteration={idx}
                            languageList={languages}
                            inputName={`requesterQuickDialLang${idx}`}
                            langs={langs}
                            setLangs={setLangs}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className='d-flex justify-content-end mt-3'>
            <Button variant='primary' onClick={saveConfig}>
              Update
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
